import React, { useEffect } from "react";
import get from "lodash/get";
import { MenuItem, Typography, useMediaQuery } from "@mui/material";
import DataGridTable from "components/Table/DataGrid";
import HiddenOn from "components/Wrappers/HiddenOn";
import { MOBILE_MAX_WIDTH_768 } from "constants/appConstants";
import { getMomentDate } from "core/utils/appUtils";
import { TaskStatus } from "pages/task/addTaskUtils";
import { Delete, EditOutlined } from "@mui/icons-material";
import BurgerMenu from "components/Menu/BurgerMenu";
import MobilePunchListView from "./punchList/MobilePunchListView";
import NewPuchItem from "./punchList/CreatePunchList";
import { useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchPunchListsAction,
  deletePunchItemAction,
} from "actions/punchListActions";
import { getPunchLists } from "selectors/punchListSelector";
import ViewPunchItem from "./punchList/ViewPunchItem";
import ViewPunchItemMobile from "./punchList/ViewPunchItem_Mobile";
import CreatePunchListMobile from "./punchList/CreatePunchList_Mobile";

export const PunchListPage = ({ propertyId, projectId }) => {
  const dispatch = useDispatch();
  const isMobile = useMediaQuery(MOBILE_MAX_WIDTH_768);

  const { totalCount, punchListItems } = useSelector(getPunchLists);

  const [pageModel, setPageModel] = React.useState({ page: 0, pageSize: 10 });

  const [addEditPunchItem, setAddEditPunchItem] = React.useState(false);
  const [viewPunchItem, setViewPunchItem] = React.useState(false);
  const [selectedPunchItem, setSelectedPunchItem] = React.useState(null);

  const onDeleteClick = (punchListId) => {
    dispatch(
      deletePunchItemAction(punchListId, {
        projectId,
        propertyId,
        pageNumber: pageModel.page + 1,
        pageSize: pageModel.pageSize,
      })
    );
  };

  const punchListColumns = [
    {
      field: "title",
      headerName: "Title",
      width: 150,
    },
    {
      field: "punchListCode",
      headerName: "Punch List #",
      width: 100,
      sortable: false,
    },
    {
      field: "description",
      headerName: "Description",
      sortable: false,
      width: 200,
    },
    {
      field: "priority",
      headerName: "Priority",
      sortable: true,
      width: 150,
      renderCell: ({ row }) => {
        const priority = get(row, "punchListPriority.punchListPriority", "");
        return (
          <TaskStatus
            status={priority || "Medium"}
            statusColors={statusColors}
          />
        );
      },
    },
    {
      field: "dueDate",
      headerName: "Due Date",
      sortable: true,
      width: 100,
      valueFormatter: (value) => {
        const dueDate = new Date(value).toISOString();
        return getMomentDate(dueDate);
      },
    },
    {
      field: "status",
      headerName: "Status",
      sortable: false,
      width: 100,
      renderCell: ({ row }) => {
        const priority = get(
          row,
          "punchListStatusType.punchListStatusType",
          ""
        );
        return priority;
      },
    },
    {
      field: "assignedMember",
      headerName: "Assigned Member",
      width: 150,
      sortable: false,
    },
    {
      field: "assigedTo",
      headerName: "Assigned To",
      width: 150,
      sortable: false,
    },
    {
      field: "actions",
      type: "actions",
      flex: 1,
      align: "right",
      getActions: (rowData) => [
        <BurgerMenu>
          <MenuItem
            onClick={() => {
              setSelectedPunchItem({ id: rowData.id, data: rowData.row });
              setAddEditPunchItem(true);
            }}
          >
            <EditOutlined /> <p className="m-0">Edit</p>
          </MenuItem>
          <MenuItem
            onClick={() => {
              onDeleteClick(rowData.row.punchListId);
            }}
          >
            <Delete /> Delete
          </MenuItem>
        </BurgerMenu>,
      ],
    },
  ];

  const fetchAllPunchLists = useCallback(
    (rest = {}) => {
      dispatch(
        fetchPunchListsAction({
          projectId,
          propertyId,
          pageNumber: pageModel.page + 1,
          pageSize: pageModel.pageSize,
          ...rest,
        })
      );
    },
    [dispatch, projectId, propertyId, pageModel]
  );

  useEffect(() => {
    fetchAllPunchLists();
  }, [fetchAllPunchLists]);

  const onPunchItemClick = (rowData) => {
    setViewPunchItem(true);
    setSelectedPunchItem({ id: rowData.punchListId, data: rowData });
  };

  const onViewCancel = () => {
    setViewPunchItem(false);
    setSelectedPunchItem(null);
  };

  const onAddEditCancel = () => {
    setAddEditPunchItem(false);
    if (!viewPunchItem) {
      setSelectedPunchItem(null);
    }
  };

  if (addEditPunchItem) {
    const create_punch_payload = {
      selectedPunchItem: selectedPunchItem,
      title: get(selectedPunchItem, "data.title", ""),
      onCancel: onAddEditCancel,
      propertyId: propertyId,
      projectId: projectId,
      params: {
        projectId,
        propertyId,
        pageNumber: pageModel.page + 1,
        pageSize: pageModel.pageSize,
      },
    };
    if (isMobile) {
      return <CreatePunchListMobile {...create_punch_payload} />;
    }
    return <NewPuchItem {...create_punch_payload} />;
  }

  if (viewPunchItem) {
    const viewPayload = {
      punchItemId: get(selectedPunchItem, "id", null),
      selectedPunchItem: selectedPunchItem,
      title: get(selectedPunchItem, "data.title", ""),
      onCancel: onViewCancel,
      onEditPunchItem: () => {
        setAddEditPunchItem(true);
      },
      propertyId,
      projectId,
    };
    if (isMobile) {
      return <ViewPunchItemMobile {...viewPayload} />;
    }
    return <ViewPunchItem {...viewPayload} />;
  }

  return (
    <>
      <div className="PL-tab-page" id="punchlist-tab-page">
        <div className="PL-page-content p-md-3 p-1" id="punchlist-page-content">
          <HiddenOn
            isHidden={isMobile}
            fallback={
              <MobilePunchListView
                pageModel={pageModel}
                setPageModel={setPageModel}
                totalCount={totalCount}
                punchListItems={punchListItems}
                onRowClick={(punchItem) => {
                  onPunchItemClick(punchItem);
                }}
                createPunchItem={() => {
                  setAddEditPunchItem(true);
                }}
              />
            }
            id="punchlist-desktop"
          >
            <div
              className="PL-page-header d-flex justify-content-between align-center m-3"
              id="punchlist-page-header"
            >
              <Typography className="h3">Punch List</Typography>
              <button
                className="primaryButton"
                id="create-punchlist-button"
                onClick={() => {
                  setAddEditPunchItem(true);
                }}
              >
                Create Punch List
              </button>
            </div>
            <DataGridTable
              columns={punchListColumns}
              data={punchListItems}
              showToolbar={false}
              getRowId={(row) => row.punchListId}
              checkboxSelection={false}
              onRowClick={({ row }) => {
                onPunchItemClick(row);
              }}
              pagination={totalCount > 10}
              totalCount={totalCount}
              paginationModel={pageModel}
              onPaginationChange={(props) => {
                setPageModel(props);
              }}
              onSortChange={(newSortModel) => {
                fetchAllPunchLists(newSortModel);
              }}
              // onRowClick={(rowData) => {
              // 	setViewPunchItem(true);
              // 	setSelectedPunchItem({ id: rowData.id, data: rowData.row });
              // }}
            />
          </HiddenOn>
        </div>
      </div>
    </>
  );
};

export default PunchListPage;

export const statusColors = {
  High: "#FF5630",
  Medium: "#FF9500",
  Low: "#36B37E",
};
